import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import type { ApiResponse, JournalEntry, LineType, TaxType } from '../types';

export const taxTypeLabels: Record<TaxType, string> = {
  taxable10: '課税10%',
  taxable8: '課税8%軽減',
  exempt: '免税',
  non_taxable: '非課税',
  zero_rated: '不課税',
};

export type JournalEntryFilters = {
  dateFrom?: string;
  dateTo?: string;
  description?: string;
  counterpartyId?: string;
  fiscalYear?: number;
  status?: 'active' | 'voided';
};

function buildQuery(filters: JournalEntryFilters): string {
  const params = new URLSearchParams();
  if (filters.dateFrom) params.set('dateFrom', filters.dateFrom);
  if (filters.dateTo) params.set('dateTo', filters.dateTo);
  if (filters.description) params.set('description', filters.description);
  if (filters.counterpartyId) params.set('counterpartyId', filters.counterpartyId);
  if (filters.fiscalYear !== undefined) params.set('fiscalYear', String(filters.fiscalYear));
  if (filters.status) params.set('status', filters.status);
  const q = params.toString();
  return q ? `?${q}` : '';
}

export function useJournalEntries(filters: JournalEntryFilters = {}) {
  return useQuery({
    queryKey: ['journal-entries', filters],
    queryFn: async () => {
      const res = await api.get<ApiResponse<JournalEntry[]>>(
        `/api/journal-entries${buildQuery(filters)}`,
      );
      return res.data;
    },
  });
}

export type JournalEntryInput = {
  entryDate: string;
  description: string;
  taxType: TaxType;
  isTaxInclusive: boolean;
  counterpartyId?: string | null;
  receivedInvoiceNumber?: string | null;
  lines: { lineType: LineType; accountId: string; amount: number }[];
};

export function useCreateJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JournalEntryInput) =>
      api.post<ApiResponse<JournalEntry>>('/api/journal-entries', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}

export function useVoidJournalEntry() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, voidReason }: { id: string; voidReason: string }) =>
      api.post(`/api/journal-entries/${id}/void`, { voidReason }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['journal-entries'] }),
  });
}
